// src/pages/Admin.js
import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { isAdminEmail } from '../config/admins';
import {
  getAllCertificates, getPendingCertificates,
  approveCertificate, rejectCertificate,
  getDoctors, saveDoctor, updateDoctor, deleteDoctor, uploadDoctorSignature,
  uploadProductLabel,
  adminCreateUser, getAllUsers, duplicateCertificate,
} from '../firebase/services';
import StatusBadge from '../components/StatusBadge';
import ChromatogramEditor from '../components/ChromatogramEditor';
import RichTextEditor from '../components/RichTextEditor';
import { CRYPTO_WALLETS, CERT_TYPES } from '../config/site';
import PdfPreviewStep from '../components/PdfPreviewStep';

const TABS = ['pending', 'all', 'doctors', 'users'];

export default function Admin() {
  const { user } = useAuth();
  const { t, lang } = useLang();

  if (!isAdminEmail(user?.email)) {
    return (
      <div style={s.page}>
        <div style={s.deniedBox}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>🔒</div>
          <h2 style={s.deniedTitle}>{lang === 'de' ? 'Zugriff verweigert' : 'Access Denied'}</h2>
          <p style={s.deniedText}>{lang === 'de' ? 'Keine Administratorrechte.' : "You don't have administrator privileges."}</p>
        </div>
      </div>
    );
  }

  const [tab, setTab] = useState('pending');

  const tabLabels = {
    en: { pending: '⏳ Pending Review', all: '📋 All Certificates', doctors: '👨‍⚕️ Doctors', users: '👥 Users' },
    de: { pending: '⏳ Ausstehend', all: '📋 Alle Zertifikate', doctors: '👨‍⚕️ Ärzte', users: '👥 Benutzer' },
  }[lang] || { pending: '⏳ Pending Review', all: '📋 All Certificates', doctors: '👨‍⚕️ Doctors' };

  return (
    <div style={s.page}>
      <h1 style={s.title}>{lang === 'de' ? 'Admin-Panel' : 'Admin Panel'}</h1>
      <div style={s.tabBar}>
        {TABS.map(tb => (
          <button key={tb} style={{ ...s.tab, ...(tab === tb ? s.tabActive : {}) }} onClick={() => setTab(tb)}>
            {tabLabels[tb]}
          </button>
        ))}
      </div>
      {(tab === 'pending' || tab === 'all') && <CertsTab tab={tab} user={user} t={t} lang={lang} />}
      {tab === 'doctors' && <DoctorsTab lang={lang} />}
      {tab === 'users'   && <UsersTab   lang={lang} />}
    </div>
  );
}

// ── Certificates tab ──────────────────────────────────────────────────────────
function CertsTab({ tab, user, t, lang }) {
  const [certs, setCerts]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [doctors, setDoctors]     = useState([]);
  const [rejectModal, setRejectModal]   = useState(null);
  const [search,       setSearch]        = useState('');
  const [dupModal,     setDupModal]      = useState(null);  // cert being duplicated
  const [users,        setUsers]         = useState([]);
  const [selectedUser, setSelectedUser]  = useState('');
  const [dupLoading,   setDupLoading]    = useState(false);
  const [dupSearch,    setDupSearch]     = useState('');
  const [pdfLoading,   setPdfLoading]    = useState(null); // certId being generated
  const [approveModal, setApproveModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [selectedDoctor, setSelectedDoctor]     = useState('');
  const [approveStep, setApproveStep]           = useState(0); // 0=results,1=charts,2=notes,3=doctor
  const [charts, setCharts]                     = useState([]); // [{url,title}]
  const [richText, setRichText]                 = useState({ html: '', text: '', sections: [] });
  const [results, setResults] = useState({
    purity: '', totalContent: '', expectedContent: '',
    fillAccuracy: '', appearance: '', notes: '',
    paramResults: {}, // { [paramKey]: { result, pass } }
  });
  const [processingId, setProcessingId]     = useState(null);
  const [editedSample,  setEditedSample]    = useState(null);  // editable copy of cert data
  const [editedContact, setEditedContact]   = useState(null);
  const [pdfPreviewMode, setPdfPreviewMode] = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl]  = useState(null);
  const [editedLabelImage, setEditedLabelImage] = useState(null); // base64 of new label
  const [editedLabelUrl,   setEditedLabelUrl]   = useState(null); // existing label URL
  const [pdfSpacing, setPdfSpacing]         = useState({
    afterSpecs: 0, afterResults: 0, afterContact: 0, beforeDoctor: 0, afterCharts: 0, afterMethods: 0,
  });

  const tx = {
    en: {
      noPending: 'No certificates waiting for review.',
      noAll: 'No certificates found.',
      approve: 'Approve', reject: 'Reject',
      submittedBy: 'Submitted by', submittedOn: 'Submitted on',
      rejectTitle: 'Reject Certificate',
      rejectDesc: 'Provide a reason — the user will see this.',
      rejectPlaceholder: 'e.g. Missing batch number, incorrect CAS...',
      cancel: 'Cancel', confirmReject: 'Confirm Rejection',
      approveTitle: 'Approve Certificate',
      approveDesc: 'Select the doctor who reviewed and approved this certificate.',
      selectDoctor: 'Select reviewing doctor',
      noDoctors: 'No doctors registered yet. Add them in the Doctors tab.',
      confirmApprove: 'Confirm Approval',
      viewSpecs: 'View details',
    },
    de: {
      noPending: 'Keine Zertifikate zur Prüfung.',
      noAll: 'Keine Zertifikate gefunden.',
      approve: 'Genehmigen', reject: 'Ablehnen',
      submittedBy: 'Eingereicht von', submittedOn: 'Eingereicht am',
      rejectTitle: 'Zertifikat ablehnen',
      rejectDesc: 'Geben Sie einen Grund an — der Benutzer sieht diesen.',
      rejectPlaceholder: 'z.B. Fehlende Chargennummer...',
      cancel: 'Abbrechen', confirmReject: 'Ablehnung bestätigen',
      approveTitle: 'Zertifikat genehmigen',
      approveDesc: 'Wählen Sie den Arzt, der dieses Zertifikat geprüft hat.',
      selectDoctor: 'Prüfenden Arzt auswählen',
      noDoctors: 'Noch keine Ärzte registriert. Fügen Sie sie im Tab "Ärzte" hinzu.',
      confirmApprove: 'Genehmigung bestätigen',
      viewSpecs: 'Details anzeigen',
    },
  };
  const t2 = tx[lang] || tx.en;

  const loadData = async () => {
    setLoading(true);
    try {
      const [certsData, docsData, usersData] = await Promise.all([
        tab === 'pending' ? getPendingCertificates() : getAllCertificates(),
        getDoctors(),
        getAllUsers(),
      ]);
      setUsers(usersData);
      setCerts(certsData);
      setDoctors(docsData);
      console.log('Doctors loaded:', docsData.length, docsData.map(d => d.name));
    } catch (e) {
      console.error('loadData error:', e?.code, e?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [tab]);

  const formatDate = (ts) => ts?.seconds ? new Date(ts.seconds * 1000).toLocaleString() : '—';

  const openApproveModal = (cert) => {
    setApproveModal(cert);
    setApproveStep(0);
    setPdfPreviewMode(false);
    setPdfSpacing({ afterSpecs:0, afterResults:0, afterContact:0, beforeDoctor:0, afterCharts:0, afterMethods:0 });
    setPreviewBlobUrl(null);
    setEditedLabelImage(null);
    setEditedLabelUrl(cert.productLabelUrl || null);

    // Pre-load sample/contact from cert (works for duplicated certs too)
    setEditedSample(cert.sample  ? { ...cert.sample  } : null);
    setEditedContact(cert.contact ? { ...cert.contact } : null);

    // If cert has prior data (e.g. duplicated with analysis pre-filled), load it
    // Otherwise start fresh
    const prevDoctor = doctors.find(d => d.name === cert.doctorName);
    setSelectedDoctor(prevDoctor?.id || doctors[0]?.id || '');

    setResults(cert.results ? {
      purity:          cert.results.purity          || '',
      totalContent:    cert.results.totalContent    || '',
      expectedContent: cert.results.expectedContent || '',
      fillAccuracy:    cert.results.fillAccuracy    || '',
      appearance:      cert.results.appearance      || '',
      notes:           cert.results.notes           || '',
      paramResults:    cert.results.paramResults    || {},
    } : {
      purity: '', totalContent: '', expectedContent: '',
      fillAccuracy: '', appearance: '', notes: '', paramResults: {},
    });

    setCharts(cert.charts   || []);
    setRichText(cert.richText || { html: '', text: '', sections: [] });
  };

  // Opens the wizard pre-loaded with existing approved data for editing
  const openEditApprovedModal = (cert) => {
    setApproveModal(cert);

    // Pre-select the doctor that was previously assigned
    const prevDoctor = doctors.find(d => d.name === cert.doctorName);
    setSelectedDoctor(prevDoctor?.id || doctors[0]?.id || '');

    setApproveStep(0);
    setPdfPreviewMode(false);
    setPdfSpacing({ afterSpecs:0, afterResults:0, afterContact:0, beforeDoctor:0, afterCharts:0, afterMethods:0 });

    setEditedLabelImage(null);
    setEditedLabelUrl(cert.productLabelUrl || null);
    // Pre-load existing results
    setResults(cert.results ? {
      purity:          cert.results.purity          || '',
      totalContent:    cert.results.totalContent    || '',
      expectedContent: cert.results.expectedContent || '',
      fillAccuracy:    cert.results.fillAccuracy    || '',
      appearance:      cert.results.appearance      || '',
      notes:           cert.results.notes           || '',
      paramResults:    cert.results.paramResults    || {},
    } : {
      purity: '', totalContent: '', expectedContent: '',
      fillAccuracy: '', appearance: '', notes: '', paramResults: {},
    });

    // Pre-load existing charts (URLs — admin can adjust in editor)
    setCharts(cert.charts || []);

    // Pre-load existing rich text / methods
    setRichText(cert.richText || { html: '', text: '', sections: [] });

    // Pre-load client data for editing
    setEditedSample(cert.sample  ? { ...cert.sample  } : null);
    setEditedContact(cert.contact ? { ...cert.contact } : null);
  };

  const handleConfirmApprove = async () => {
    if (!approveModal) return;
    const doctor = doctors.find(d => d.id === selectedDoctor) || null;
    setProcessingId(approveModal.id);

    // Upload all charts to Storage
    const uploadedCharts = [];
    for (let i = 0; i < charts.length; i++) {
      const ch = charts[i];
      if (!ch?.url) continue;
      try {
        const { ref: sRef, uploadString: uStr, getDownloadURL: gUrl } = await import('firebase/storage');
        const { storage } = await import('../firebase/config');
        const cgRef = sRef(storage, `chromatograms/${approveModal.certId}_${i}.png`);
        await uStr(cgRef, ch.url, 'data_url');
        const url = await gUrl(cgRef);
        uploadedCharts.push({ url, title: ch.title });
      } catch (e) { console.warn('Chart upload failed:', e); }
    }


    // Upload new label if admin changed it
    let finalLabelUrl = editedLabelUrl || approveModal.productLabelUrl || '';
    // Only upload if it's a real base64 image (not an existing URL)
    if (editedLabelImage && editedLabelImage.startsWith('data:')) {
      try {
        finalLabelUrl = await uploadProductLabel(approveModal.certId, editedLabelImage);
        console.log('Label uploaded successfully:', finalLabelUrl);
      } catch (e) { console.error('Label upload failed:', e); }
    } else {
      console.log('No new label to upload. editedLabelImage:', typeof editedLabelImage, editedLabelImage?.slice?.(0,30));
    }

    // Build updatedCert with all edited data for Firestore
    const updatedCertForFirestore = {
      ...approveModal,
      sample:         editedSample  || approveModal.sample,
      contact:        editedContact || approveModal.contact,
      productLabelUrl: finalLabelUrl,
    };

    // ── SAVE TO FIRESTORE ──────────────────────────────────────────────────
    await approveCertificate(
      approveModal.id,
      user.email,
      doctor,
      results,
      uploadedCharts,
      richText,
      updatedCertForFirestore
    );

    // Update local state so UI reflects changes without refresh
    const fullyUpdatedCert = {
      ...approveModal,
      status:             'approved',
      sample:             editedSample  || approveModal.sample,
      contact:            editedContact || approveModal.contact,
      results,
      charts:             uploadedCharts,
      richText,
      doctorId:           doctor?.id           || '',
      doctorName:         doctor?.name         || '',
      doctorTitle:        doctor?.title        || '',
      doctorSignatureUrl: doctor?.signatureUrl || '',
      productLabelUrl:    finalLabelUrl,
    };

    setCerts(cs => tab === 'pending'
      ? cs.filter(c => c.id !== approveModal.id)
      : cs.map(c => c.id === approveModal.id ? fullyUpdatedCert : c)
    );
    setProcessingId(null);
    setApproveModal(null);
  };

  const openRejectModal = (cert) => { setRejectModal(cert); setRejectReason(''); };

  const openDupModal = (cert) => {
    setDupModal(cert);
    setSelectedUser(users[0]?.uid || '');
    setDupSearch('');
  };

  const handleGeneratePdf = async (cert) => {
    setPdfLoading(cert.id);
    try {
      const { generatePDF } = await import('../utils/pdfGenerator');
      const { getUserLogo }  = await import('./Settings');
      const doctor = doctors.find(d => d.name === cert.doctorName);
      await generatePDF({
        certId:             cert.certId,
        includeQR:          true,
        includeLogo:        true,
        logoDataUrl:        getUserLogo(),
        productLabelUrl:    cert.productLabelUrl || '',
        doctorName:         doctor?.name         || cert.doctorName         || '',
        doctorTitle:        doctor?.title        || cert.doctorTitle        || '',
        doctorSignatureUrl: doctor?.signatureUrl || cert.doctorSignatureUrl || '',
        results:            cert.results,
        charts:             cert.charts   || [],
        richText:           cert.richText || null,
        contact:            cert.contact,
        sample:             cert.sample,
        analysis:           cert.analysis,
        product:            cert.product,
        specs:              cert.specs,
        lab:                cert.lab,
      }, cert.lang || lang);
    } catch(e) { console.error('PDF error:', e); alert('PDF generation failed: ' + e.message); }
    setPdfLoading(null);
  };

  const handleDuplicate = async () => {
    if (!dupModal || !selectedUser) return;
    setDupLoading(true);
    const target = users.find(u => u.uid === selectedUser);
    try {
      const { certId: newId } = await duplicateCertificate(dupModal, selectedUser, target?.email || '', target?.name || '');
      alert(`✓ Certificate duplicated as ${newId} for ${target?.email}`);
      setDupModal(null);
      loadData();
    } catch (e) { alert('Error: ' + e.message); }
    finally { setDupLoading(false); }
  };

  const handleConfirmReject = async () => {
    if (!rejectModal) return;
    setProcessingId(rejectModal.id);
    await rejectCertificate(rejectModal.id, user.email, rejectReason);
    setCerts(cs => tab === 'pending'
      ? cs.filter(c => c.id !== rejectModal.id)
      : cs.map(c => c.id === rejectModal.id ? { ...c, status: 'rejected', rejectionReason: rejectReason } : c)
    );
    setProcessingId(null);
    setRejectModal(null);
  };

  if (loading) return <div style={s.empty}>Loading...</div>;
  // Filter by search
  const filteredCerts = certs.filter(cert => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      cert.certId?.toLowerCase().includes(q) ||
      cert.sample?.productName?.toLowerCase().includes(q) ||
      cert.product?.chemName?.toLowerCase().includes(q) ||
      cert.userEmail?.toLowerCase().includes(q) ||
      cert.contact?.companyName?.toLowerCase().includes(q)
    );
  });

  if (filteredCerts.length === 0 && !search) return <div style={s.empty}>{tab === 'pending' ? t2.noPending : t2.noAll}</div>;

  // Get user initials + color for avatar — uses user's name from users list
  const getUserAvatar = (cert) => {
    const email    = cert.userEmail || '';
    const userRec  = users.find(u => u.email?.toLowerCase() === email.toLowerCase());
    const name     = userRec?.name || email;
    const initials = name.split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase() || '?';
    const colors   = ['#1E5EFF','#7C3AED','#DB2777','#D97706','#059669','#DC2626','#0891B2'];
    const idx      = email.charCodeAt(0) % colors.length;
    return { initials, color: colors[idx], tooltip: userRec?.name ? `${userRec.name} (${email})` : email };
  };

  return (
    <>
      {/* Search bar */}
      <div style={{ marginBottom: 16, position: 'relative' }}>
        <span style={{ position:'absolute', left:14, top:'50%', transform:'translateY(-50%)', fontSize:16, opacity:0.4 }}>🔍</span>
        <input
          style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:12, padding:'11px 14px 11px 42px', color:'#fff', fontSize:14, outline:'none', boxSizing:'border-box', fontFamily:'inherit' }}
          placeholder={lang === 'de' ? 'Suchen nach Cert-ID, Produkt, Email...' : 'Search by cert ID, product, email...'}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && <button onClick={() => setSearch('')} style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', color:'rgba(255,255,255,0.4)', fontSize:18, cursor:'pointer' }}>✕</button>}
      </div>
      {filteredCerts.length === 0 && <div style={s.empty}>No results for "{search}"</div>}
      <div style={s.list}>
        {filteredCerts.map(cert => (
          <div key={cert.id} style={s.card}>
            <div style={s.cardTop}>
              {/* User avatar */}
              {(() => { const av = getUserAvatar(cert); return (
                <div title={av.tooltip} style={{ width:38, height:38, borderRadius:'50%', background:av.color, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:14, color:'#fff', flexShrink:0, marginRight:12, cursor:'default' }}>
                  {av.initials}
                </div>
              ); })()}
              <div style={{ flex:1 }}>
                <span style={s.certId}>{cert.certId}</span>
                <h3 style={s.product}>{cert.sample?.productName || cert.product?.chemName || '—'}</h3>
              </div>
              <StatusBadge status={cert.status || 'pending'} t={t} />
            </div>

            <div style={s.metaGrid}>
              <div><div style={s.metaLabel}>{t2.submittedBy}</div><div style={s.metaValue}>{cert.userEmail || '—'}</div></div>
              <div><div style={s.metaLabel}>{t2.submittedOn}</div><div style={s.metaValue}>{formatDate(cert.createdAt)}</div></div>
              <div><div style={s.metaLabel}>{lang === 'de' ? 'Charge' : 'Batch'}</div><div style={s.metaValue}>{cert.sample?.lot || cert.product?.batch || '—'}</div></div>
              <div><div style={s.metaLabel}>{lang === 'de' ? 'Typ' : 'Type'}</div><div style={s.metaValue}>{CERT_TYPES.find(c => c.key === cert.certType)?.name[lang] || cert.certType || '—'}</div></div>
            </div>

            {/* Sample info */}
            {cert.sample?.productName && (
              <div style={s.sampleBlock}>
                {[
                  ['Supplier', cert.sample.supplier],
                  ['Presentation', cert.sample.presentation],
                  ['Sample Qty', cert.sample.sampleQty],
                  ['Active Ingredient', cert.sample.activeIngredient],
                  ['Line', cert.sample.line],
                  ['Category', cert.sample.category],
                  ['Sample Type', Array.isArray(cert.sample.sampleType) ? cert.sample.sampleType.join(', ') : ''],
                  ['Methodology', Array.isArray(cert.sample.methodology) ? cert.sample.methodology.join(', ') : ''],
                  ['Storage', cert.sample.storageCondition],
                  ['Mfg. Date', cert.sample.mfgDate],
                  ['Exp. Date', cert.sample.expDate],
                ].filter(([,v]) => v && typeof v === 'string').map(([lbl, val]) => (
                  <div key={lbl} style={s.sampleRow}>
                    <span style={s.paymentLabel}>{lbl}</span>
                    <span style={s.paymentVal}>{val}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Product label */}
            {cert.productLabelUrl && (
              <div style={{ marginBottom: 12 }}>
                <div style={s.paymentLabel}>🏷️ {lang === 'de' ? 'Produktetikett' : 'Product Label'}</div>
                <a href={cert.productLabelUrl} target="_blank" rel="noopener noreferrer">
                  <img src={cert.productLabelUrl} alt="label" style={{ maxWidth: '100%', maxHeight: 160, borderRadius: 8, marginTop: 6, border: '1px solid rgba(255,255,255,0.1)', cursor: 'zoom-in', display: 'block' }} />
                </a>
              </div>
            )}

            {/* Analysis */}
            {cert.analysis && (
              <details style={s.details}>
                <summary style={s.summary}>
                  {t2.viewSpecs} ({Object.values(cert.analysis).filter(v => typeof v === 'object' && v?.selected).length} {lang === 'de' ? 'Analysen' : 'analyses'})
                </summary>
                <table style={s.specsTable}>
                  <thead><tr>
                    <th style={s.specsHead}>Parameter</th>
                    <th style={s.specsHead}>Specification</th>
                  </tr></thead>
                  <tbody>
                    {Object.entries(cert.analysis)
                      .filter(([k,v]) => k !== 'observations' && typeof v === 'object' && v?.selected)
                      .map(([k,v]) => (
                        <tr key={k}>
                          <td style={s.specsTd}>{k}</td>
                          <td style={s.specsTd}>{String(v.spec || '—')}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                {cert.analysis.observations && typeof cert.analysis.observations === 'string' && (
                  <div style={{ padding: '8px 0', fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
                    <strong>Observations:</strong> {cert.analysis.observations}
                  </div>
                )}
              </details>
            )}

            {/* Specs (legacy) */}
            {!cert.sample?.productName && cert.specs?.length > 0 && (
              <details style={s.details}>
                <summary style={s.summary}>{t2.viewSpecs} ({cert.specs.length})</summary>
                <table style={s.specsTable}>
                  <thead><tr>
                    {['parameter','specification','result','unit','method'].map(c => (
                      <th key={c} style={s.specsHead}>{c}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {cert.specs.map((sp, i) => (
                      <tr key={i}>
                        {['parameter','specification','result','unit','method'].map(c => (
                          <td key={c} style={s.specsTd}>{sp[c] || '—'}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}

            {/* Payment info */}
            {cert.paymentTxHash && (
              <div style={s.paymentBox}>
                <div style={s.paymentTitle}>💳 {lang === 'de' ? 'Zahlungsinformationen' : 'Payment Information'}</div>
                <div style={s.paymentRow}>
                  <span style={s.paymentLabel}>{lang === 'de' ? 'Betrag' : 'Amount'}</span>
                  <span style={{ ...s.paymentVal, color: '#FBBF24', fontWeight: 800 }}>${cert.certPrice} USD</span>
                </div>
                <div style={s.paymentRow}>
                  <span style={s.paymentLabel}>{lang === 'de' ? 'Krypto' : 'Crypto'}</span>
                  <span style={s.paymentVal}>{cert.paymentCrypto?.toUpperCase()}</span>
                </div>
                <div style={s.paymentRow}>
                  <span style={s.paymentLabel}>TX Hash</span>
                  <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', flex: 1 }}>
                    <code style={s.txHashCode}>{cert.paymentTxHash}</code>
                    {(() => {
                      const wallet = CRYPTO_WALLETS.find(w => w.key === cert.paymentCrypto);
                      return wallet ? (
                        <a href={wallet.explorerUrl + cert.paymentTxHash} target="_blank" rel="noopener noreferrer" style={s.explorerLink}>
                          🔍 {wallet.explorerName} ↗
                        </a>
                      ) : null;
                    })()}
                  </span>
                </div>
                {cert.paymentScreenshot && (
                  <div style={{ marginTop: 10 }}>
                    <div style={s.paymentLabel}>{lang === 'de' ? 'Zahlungsnachweis' : 'Payment Proof'}</div>
                    <a href={cert.paymentScreenshot} target="_blank" rel="noopener noreferrer">
                      <img src={cert.paymentScreenshot} alt="Payment proof" style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 8, marginTop: 6, border: '1px solid rgba(255,255,255,0.1)', cursor: 'zoom-in', display: 'block' }} />
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Doctor assigned (if already approved) */}
            {cert.doctorName && (
              <div style={s.doctorBadge}>
                <span>👨‍⚕️ {lang === 'de' ? 'Arzt' : 'Doctor'}: <strong>{cert.doctorName}</strong></span>
                {cert.doctorTitle && <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}> — {cert.doctorTitle}</span>}
              </div>
            )}

            {cert.status === 'rejected' && cert.rejectionReason && (
              <div style={s.rejectedNote}>
                <strong>{t('coa.pending.rejectionReason')}:</strong> {cert.rejectionReason}
              </div>
            )}

            {cert.status === 'pending' && (
              <div style={s.cardActions}>
                <button style={s.approveBtn} onClick={() => openApproveModal(cert)} disabled={processingId === cert.id}>
                  {processingId === cert.id ? '...' : `✓ ${t2.approve}`}
                </button>
                <button style={s.rejectBtn} onClick={() => openRejectModal(cert)} disabled={processingId === cert.id}>
                  ✕ {t2.reject}
                </button>
              </div>
            )}

            {cert.status === 'approved' && (
              <div style={{ marginTop: 8, display:'flex', flexDirection:'column', gap:6 }}>
                <button
                  style={{ ...s.reEditBtn, width:'100%' }}
                  onClick={() => openEditApprovedModal(cert)}
                  disabled={processingId === cert.id}>
                  ✏️ {lang === 'de' ? 'Bearbeiten & Neu genehmigen' : 'Edit & Re-approve'}
                </button>
                <div style={{ display:'flex', gap:6 }}>
                  <button
                    style={{ ...s.reEditBtn, flex:1, background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.25)', color:'#4ADE80', fontSize:12 }}
                    onClick={() => handleGeneratePdf(cert)}
                    disabled={pdfLoading === cert.id}>
                    {pdfLoading === cert.id ? '⏳...' : '📥 PDF'}
                  </button>
                  <button
                    style={{ ...s.reEditBtn, flex:1, background:'rgba(124,58,237,0.1)', border:'1px solid rgba(124,58,237,0.25)', color:'#A78BFA', fontSize:12 }}
                    onClick={() => openDupModal(cert)}>
                    ⧉ {lang === 'de' ? 'Duplizieren' : 'Duplicate'}
                  </button>
                </div>
              </div>
            )}

            {cert.status === 'pending' && (
              <button
                style={{ marginTop:6, ...s.reEditBtn, background:'rgba(124,58,237,0.08)', border:'1px solid rgba(124,58,237,0.2)', color:'#A78BFA', fontSize:12 }}
                onClick={() => openDupModal(cert)}>
                ⧉ {lang === 'de' ? 'Duplizieren' : 'Duplicate'}
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Approve modal — Step wizard: must complete all steps */}
      {approveModal && (() => {
        const STEPS = [
          { key: 'edit',    icon: '✏️',  label: lang === 'de' ? 'Daten bearbeiten' : 'Edit Data'     },
          { key: 'results', icon: '📊',  label: lang === 'de' ? 'Ergebnisse'       : 'Results'       },
          { key: 'charts',  icon: '📈',  label: lang === 'de' ? 'Diagramme'        : 'Charts'        },
          { key: 'notes',   icon: '📝',  label: lang === 'de' ? 'Methoden'         : 'Methods/Notes' },
          { key: 'doctor',  icon: '👨‍⚕️', label: lang === 'de' ? 'Arzt'             : 'Doctor'        },
          { key: 'preview', icon: '👁️',  label: lang === 'de' ? 'PDF Vorschau'     : 'PDF Preview'   },
        ];
        const allDone = results.purity && selectedDoctor;
        return (
          <div style={s.modalOverlay} onClick={() => setApproveModal(null)}>
            <div style={{ ...s.modal, maxWidth: approveStep === 5 ? 1100 : 700, maxHeight: '96vh', overflowY: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>

              {/* ── Fixed header: title + steps always visible ── */}
              <div style={{ flexShrink: 0, paddingBottom: 14, borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: 16 }}>
                <h3 style={{ ...s.modalTitle, marginBottom: 4 }}>{approveModal.status === 'approved' ? '✏️ ' + (lang === 'de' ? 'Zertifikat bearbeiten' : 'Edit Certificate') : '✓ ' + t2.approveTitle}</h3>
                <p style={{ ...s.modalCertId, marginBottom: 12 }}>{approveModal.certId} — {approveModal.sample?.productName || approveModal.product?.chemName}</p>

                {/* Step indicator — always visible */}
                <div style={{ ...s.wizardSteps, marginBottom: 0 }}>
                  {STEPS.map((st, i) => (
                    <React.Fragment key={st.key}>
                      <button
                        style={{ ...s.wizardStep, ...(i === approveStep ? s.wizardStepActive : i < approveStep ? s.wizardStepDone : {}) }}
                        onClick={() => setApproveStep(i)}
                      >
                        <span style={s.wizardStepNum}>{i < approveStep ? '✓' : i + 1}</span>
                        <span style={s.wizardStepLabel}>{st.icon} {st.label}</span>
                      </button>
                      {i < STEPS.length - 1 && <div style={{ ...s.wizardLine, ...(i < approveStep ? s.wizardLineDone : {}) }} />}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Scrollable step content */}
              <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 8 }}>
              {/* Step 0: Edit client data */}
              {approveStep === 0 && (
                <EditDataStep
                  cert={approveModal}
                  editedSample={editedSample}
                  editedContact={editedContact}
                  setEditedSample={setEditedSample}
                  setEditedContact={setEditedContact}
                  editedLabelImage={editedLabelImage}
                  setEditedLabelImage={setEditedLabelImage}
                  editedLabelUrl={editedLabelUrl}
                  setEditedLabelUrl={setEditedLabelUrl}
                  lang={lang}
                />
              )}

              {/* Step 1: Results */}
              {approveStep === 1 && (
                <div>
                  <p style={s.modalDesc}>{lang === 'de' ? 'Geben Sie die tatsächlichen Analyseergebnisse ein.' : 'Enter the actual analysis results from the lab.'}</p>
                  <div style={s.resultsGrid}>
                    {[
                      ['purity',          lang === 'de' ? 'Reinheit (%) *'        : 'Purity (%) *',         '99.15'],
                      ['totalContent',    lang === 'de' ? 'Gesamtgehalt'          : 'Total Content',        '9.93 mg'],
                      ['expectedContent', lang === 'de' ? 'Erwarteter Gehalt'     : 'Expected Content',     '10 mg'],
                      ['fillAccuracy',    lang === 'de' ? 'Füllgenauigkeit (%)'   : 'Fill Accuracy (%)',    '99.3'],
                      ['appearance',      lang === 'de' ? 'Aussehen'              : 'Appearance',           'White lyophilized powder'],
                    ].map(([key, label, ph]) => (
                      <div key={key}>
                        <label style={s.formLabel}>{label}</label>
                        <input style={s.formInput} placeholder={ph} value={results[key]}
                          onChange={e => setResults(r => ({ ...r, [key]: e.target.value }))} />
                      </div>
                    ))}
                  </div>
                  {approveModal.analysis && (
                    <div style={{ marginTop: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#4A9EFF', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {lang === 'de' ? 'Ergebnis je Parameter' : 'Result per requested parameter'}
                      </div>
                      {Object.entries(approveModal.analysis)
                        .filter(([k,v]) => k !== 'observations' && typeof v === 'object' && v?.selected)
                        .map(([k, v]) => (
                          <div key={k} style={s.paramRow}>
                            <span style={s.paramName}>{k}</span>
                            <span style={s.paramSpec}>{v.spec || '—'}</span>
                            <input style={s.paramInput} placeholder={lang === 'de' ? 'Ergebnis...' : 'Result...'}
                              value={results.paramResults?.[k]?.result || ''}
                              onChange={e => setResults(r => ({ ...r, paramResults: { ...r.paramResults, [k]: { ...r.paramResults?.[k], result: e.target.value } } }))} />
                            <select style={s.paramPass}
                              value={results.paramResults?.[k]?.pass ?? 'pass'}
                              onChange={e => setResults(r => ({ ...r, paramResults: { ...r.paramResults, [k]: { ...r.paramResults?.[k], pass: e.target.value } } }))}>
                              <option value="pass">✓ PASS</option>
                              <option value="fail">✕ FAIL</option>
                            </select>
                          </div>
                        ))}
                    </div>
                  )}
                  <div style={{ marginTop: 14 }}>
                    <label style={s.formLabel}>{lang === 'de' ? 'Anmerkungen' : 'Notes'}</label>
                    <textarea style={{ ...s.formInput, minHeight: 60, resize: 'vertical' }}
                      value={results.notes} onChange={e => setResults(r => ({ ...r, notes: e.target.value }))}
                      placeholder={lang === 'de' ? 'Zusätzliche Kommentare...' : 'Additional comments...'} />
                  </div>
                </div>
              )}

              {/* Step 2: Charts */}
              {approveStep === 2 && (
                <ChromatogramEditor lang={lang}
                  initialCharts={charts}
                  onChange={chartList => setCharts(chartList || [])} />
              )}

              {/* Step 3: Methods / Notes */}
              {approveStep === 3 && (
                <RichTextEditor lang={lang}
                  initialHtml={richText?.html || ''}
                  onChange={data => setRichText(data)} />
              )}

              {/* Step 4: Doctor */}
              {approveStep === 4 && (
                <div>
                  <p style={s.modalDesc}>{t2.approveDesc}</p>
                  {doctors.length === 0 ? (
                    <div style={s.noDoctorsNote}>{t2.noDoctors}</div>
                  ) : (
                    <div style={s.doctorList}>
                      {doctors.map(doc => (
                        <button key={doc.id}
                          style={{ ...s.doctorOption, ...(selectedDoctor === doc.id ? s.doctorOptionActive : {}) }}
                          onClick={() => setSelectedDoctor(doc.id)}>
                          <div style={s.doctorOptionLeft}>
                            {doc.signatureUrl
                              ? <img src={doc.signatureUrl} alt="sig" style={s.doctorSigThumb} />
                              : <div style={s.doctorSigPlaceholder}>✍️</div>}
                            <div>
                              <div style={s.doctorOptionName}>{doc.name}</div>
                              <div style={s.doctorOptionTitle}>{doc.title}</div>
                            </div>
                          </div>
                          {selectedDoctor === doc.id && <span style={{ color: '#4A9EFF', fontWeight: 800 }}>✓</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Step 5: Live PDF Preview */}
              {approveStep === 5 && (
                <PdfPreviewStep
                  cert={approveModal}
                  editedSample={editedSample}
                  editedContact={editedContact}
                  editedLabelImage={editedLabelImage}
                  editedLabelUrl={editedLabelUrl}
                  results={results}
                  charts={charts}
                  richText={richText}
                  selectedDoctor={doctors.find(d => d.id === selectedDoctor)}
                  pdfSpacing={pdfSpacing}
                  setPdfSpacing={setPdfSpacing}
                  lang={lang}
                  onBlobReady={setPreviewBlobUrl}
                />
              )}

              </div>{/* end scrollable */}

              {/* Navigation — fixed at bottom */}
              <div style={{ flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)', background: '#0E1840' }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={s.cancelBtn} onClick={() => setApproveModal(null)}>{t2.cancel}</button>
                  {approveStep > 0 && (
                    <button style={s.cancelBtn} onClick={() => setApproveStep(s => s - 1)}>← {lang === 'de' ? 'Zurück' : 'Back'}</button>
                  )}
                </div>
                {approveStep < STEPS.length - 1 ? (
                  <button style={{ ...s.confirmApproveBtn, opacity: approveStep === 1 && !results.purity ? 0.5 : 1 }}
                    onClick={() => setApproveStep(s => s + 1)}
                    disabled={approveStep === 1 && !results.purity}>
                    {lang === 'de' ? 'Weiter →' : 'Next →'}
                  </button>
                ) : (
                  <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                    {approveStep === 5 && (
                      <a href={previewBlobUrl || '#'}
                        download={approveModal.certId + '.pdf'}
                        style={{
                          padding:'10px 16px', borderRadius:10, fontSize:13, fontWeight:700,
                          background: previewBlobUrl ? 'rgba(74,158,255,0.15)' : 'rgba(255,255,255,0.05)',
                          border: previewBlobUrl ? '1px solid rgba(74,158,255,0.4)' : '1px solid rgba(255,255,255,0.1)',
                          color: previewBlobUrl ? '#4A9EFF' : 'rgba(255,255,255,0.3)',
                          textDecoration:'none', pointerEvents: previewBlobUrl ? 'auto' : 'none',
                        }}>
                        📥 DOWNLOAD PDF
                      </a>
                    )}
                    <button style={{ ...s.confirmApproveBtn, opacity: !allDone ? 0.5 : 1 }}
                      onClick={handleConfirmApprove}
                      disabled={!allDone || processingId === approveModal.id}>
                      {processingId === approveModal.id ? '...' : `✓ ${t2.confirmApprove}`}
                    </button>
                  </div>
                )}
              </div>

              {!allDone && approveStep === STEPS.length - 1 && approveStep !== 5 && (
                <p style={{ fontSize: 11, color: '#FBBF24', textAlign: 'center', marginTop: 8 }}>
                  {lang === 'de'
                    ? '⚠️ Purity % (Schritt 1) und Arzt (dieser Schritt) sind erforderlich.'
                    : '⚠️ Purity % (Step 1) and Doctor (this step) are required to approve.'}
                </p>
              )}
            </div>
          </div>
        );
      })()}

      {/* Duplicate modal */}
      {dupModal && (
        <div style={s.modalOverlay} onClick={() => setDupModal(null)}>
          <div style={{ ...s.modal, maxWidth:500 }} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitle}>⧉ {lang === 'de' ? 'Zertifikat duplizieren' : 'Duplicate Certificate'}</h3>
            <p style={s.modalCertId}>{dupModal.certId} — {dupModal.sample?.productName || dupModal.product?.chemName}</p>
            <p style={{ fontSize:13, color:'rgba(255,255,255,0.5)', marginBottom:16 }}>
              {lang === 'de'
                ? 'Wählen Sie den Benutzer, dem das duplizierte Zertifikat zugewiesen werden soll.'
                : 'Select the user to assign the duplicated certificate to. It will appear as pending in their account.'}
            </p>
            {users.length === 0
              ? <div style={{ color:'#FBBF24', fontSize:13 }}>⚠️ {lang === 'de' ? 'Keine Benutzer gefunden. Erstellen Sie zuerst Benutzer im Tab "Benutzer".' : 'No users found. Create users in the Users tab first.'}</div>
              : (<>
                <label style={{ fontSize:12, color:'rgba(255,255,255,0.5)', display:'block', marginBottom:8 }}>
                  {lang === 'de' ? 'Zielbenutzer' : 'Target user'}
                </label>
                {/* Search inside modal */}
                <div style={{ position:'relative', marginBottom:10 }}>
                  <span style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', fontSize:14, opacity:0.4 }}>🔍</span>
                  <input
                    style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, padding:'8px 10px 8px 32px', color:'#fff', fontSize:12, outline:'none', boxSizing:'border-box', fontFamily:'inherit' }}
                    placeholder={lang === 'de' ? 'Benutzer suchen...' : 'Search users...'}
                    value={dupSearch}
                    onChange={e => setDupSearch(e.target.value)}
                  />
                  {dupSearch && <button onClick={()=>setDupSearch('')} style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', color:'rgba(255,255,255,0.4)', fontSize:14, cursor:'pointer' }}>✕</button>}
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:8, maxHeight:240, overflowY:'auto', marginBottom:20 }}>
                  {users.filter(u => {
                    if (!dupSearch.trim()) return true;
                    const q = dupSearch.toLowerCase();
                    return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
                  }).map(u => (
                    <button key={u.uid} onClick={() => setSelectedUser(u.uid)}
                      style={{ display:'flex', alignItems:'center', gap:12, background: selectedUser===u.uid ? 'rgba(124,58,237,0.15)' : 'rgba(255,255,255,0.03)', border: selectedUser===u.uid ? '1px solid rgba(124,58,237,0.5)' : '1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:'10px 14px', cursor:'pointer', textAlign:'left' }}>
                      {(() => { const colors=['#1E5EFF','#7C3AED','#DB2777','#D97706','#059669']; const i=u.email?.charCodeAt(0)%5; const ini=(u.name||u.email||'?').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase(); return (
                        <div style={{ width:32, height:32, borderRadius:'50%', background:colors[i], display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:12, color:'#fff', flexShrink:0 }}>{ini}</div>
                      ); })()}
                      <div>
                        <div style={{ fontSize:13, fontWeight:600, color:'#fff' }}>{u.name || '—'}</div>
                        <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)' }}>{u.email}</div>
                      </div>
                      {selectedUser === u.uid && <span style={{ marginLeft:'auto', color:'#A78BFA', fontSize:16 }}>✓</span>}
                    </button>
                  ))}
                </div>
                <div style={{ display:'flex', gap:10 }}>
                  <button style={s.cancelBtn} onClick={() => setDupModal(null)}>{lang === 'de' ? 'Abbrechen' : 'Cancel'}</button>
                  <button style={{ ...s.confirmApproveBtn, flex:1, background:'linear-gradient(135deg,#7C3AED,#5B21B6)', opacity: selectedUser ? 1 : 0.5 }}
                    onClick={handleDuplicate} disabled={!selectedUser || dupLoading}>
                    {dupLoading ? '...' : `⧉ ${lang === 'de' ? 'Duplizieren & Zuweisen' : 'Duplicate & Assign'}`}
                  </button>
                </div>
              </>)
            }
          </div>
        </div>
      )}

      {/* Reject modal */}
      {rejectModal && (
        <div style={s.modalOverlay} onClick={() => setRejectModal(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitle}>✕ {t2.rejectTitle}</h3>
            <p style={s.modalDesc}>{t2.rejectDesc}</p>
            <p style={s.modalCertId}>{rejectModal.certId} — {rejectModal.sample?.productName || rejectModal.product?.chemName}</p>
            <textarea style={s.textarea} placeholder={t2.rejectPlaceholder} value={rejectReason} onChange={e => setRejectReason(e.target.value)} rows={4} />
            <div style={s.modalActions}>
              <button style={s.cancelBtn} onClick={() => setRejectModal(null)}>{t2.cancel}</button>
              <button style={s.confirmRejectBtn} onClick={handleConfirmReject} disabled={!rejectReason.trim() || processingId === rejectModal.id}>
                {processingId === rejectModal.id ? '...' : t2.confirmReject}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Doctors tab ───────────────────────────────────────────────────────────────
function DoctorsTab({ lang }) {
  const [doctors, setDoctors]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null); // doctor being edited
  const [form, setForm]         = useState({ name: '', title: '', specialty: '' });
  const [sigPreview, setSigPreview] = useState('');
  const [saving, setSaving]     = useState(false);
  const [toast, setToast]       = useState('');
  const sigRef = useRef();

  const tx = {
    en: {
      title: 'Doctors / Reviewers', add: '+ Add Doctor',
      name: 'Full Name', title2: 'Title / Position', specialty: 'Specialty',
      signature: 'Signature Image', sigHint: 'PNG with transparent background recommended · Max 2MB',
      uploadSig: '⬆ Upload Signature', changeSig: '⬆ Change Signature',
      save: 'Save Doctor', cancel: 'Cancel', edit: 'Edit', delete: 'Delete',
      noSig: 'No signature', deleteConfirm: 'Delete this doctor?',
      saved: 'Doctor saved!', deleted: 'Doctor deleted.',
      empty: 'No doctors yet. Add the first one.',
    },
    de: {
      title: 'Ärzte / Prüfer', add: '+ Arzt hinzufügen',
      name: 'Vollständiger Name', title2: 'Titel / Position', specialty: 'Fachgebiet',
      signature: 'Unterschriftsbild', sigHint: 'PNG mit transparentem Hintergrund empfohlen · Max. 2MB',
      uploadSig: '⬆ Unterschrift hochladen', changeSig: '⬆ Unterschrift ändern',
      save: 'Arzt speichern', cancel: 'Abbrechen', edit: 'Bearbeiten', delete: 'Löschen',
      noSig: 'Keine Unterschrift', deleteConfirm: 'Diesen Arzt löschen?',
      saved: 'Arzt gespeichert!', deleted: 'Arzt gelöscht.',
      empty: 'Noch keine Ärzte. Fügen Sie den ersten hinzu.',
    },
  };
  const t2 = tx[lang] || tx.en;

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  useEffect(() => {
    getDoctors().then(setDoctors).finally(() => setLoading(false));
  }, []);

  const handleSigUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setSigPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ name: '', title: '', specialty: '' });
    setSigPreview('');
    setShowForm(true);
  };

  const openEdit = (doc) => {
    setEditing(doc);
    setForm({ name: doc.name, title: doc.title || '', specialty: doc.specialty || '' });
    setSigPreview(doc.signatureUrl || '');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editing) {
        // Upload new signature if changed
        let signatureUrl = editing.signatureUrl || '';
        if (sigPreview && sigPreview !== editing.signatureUrl) {
          signatureUrl = await uploadDoctorSignature(editing.id, sigPreview);
        }
        await updateDoctor(editing.id, { ...form, signatureUrl });
        setDoctors(ds => ds.map(d => d.id === editing.id ? { ...d, ...form, signatureUrl } : d));
      } else {
        // Create new doctor first to get ID, then upload signature
        const newId = await saveDoctor({ ...form, signatureUrl: '' });
        let signatureUrl = '';
        if (sigPreview) {
          signatureUrl = await uploadDoctorSignature(newId, sigPreview);
          await updateDoctor(newId, { signatureUrl });
        }
        setDoctors(ds => [...ds, { id: newId, ...form, signatureUrl }]);
      }
      showToast(t2.saved);
      setShowForm(false);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(t2.deleteConfirm)) return;
    await deleteDoctor(doc.id);
    setDoctors(ds => ds.filter(d => d.id !== doc.id));
    showToast(t2.deleted);
  };

  return (
    <div>
      {toast && <div style={s.toast}>{toast}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#fff', margin: 0 }}>{t2.title}</h2>
        <button style={s.addDoctorBtn} onClick={openAdd}>{t2.add}</button>
      </div>

      {loading ? <div style={s.empty}>Loading...</div>
       : doctors.length === 0 ? <div style={s.empty}>{t2.empty}</div>
       : (
        <div style={s.doctorGrid}>
          {doctors.map(doc => (
            <div key={doc.id} style={s.doctorCard}>
              <div style={s.doctorCardTop}>
                <div>
                  <div style={s.doctorName}>{doc.name}</div>
                  <div style={s.doctorTitle}>{doc.title}</div>
                  {doc.specialty && <div style={s.doctorSpecialty}>{doc.specialty}</div>}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={s.editBtn} onClick={() => openEdit(doc)}>{t2.edit}</button>
                  <button style={s.deleteBtn} onClick={() => handleDelete(doc)}>{t2.delete}</button>
                </div>
              </div>
              <div style={s.sigSection}>
                {doc.signatureUrl ? (
                  <img src={doc.signatureUrl} alt="Signature" style={s.sigImg} />
                ) : (
                  <span style={s.noSig}>{t2.noSig}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit form modal */}
      {showForm && (
        <div style={s.modalOverlay} onClick={() => setShowForm(false)}>
          <div style={{ ...s.modal, maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <h3 style={s.modalTitle}>{editing ? t2.edit : t2.add}</h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={s.formLabel}>{t2.name} *</label>
                <input style={s.formInput} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label style={s.formLabel}>{t2.title2}</label>
                <input style={s.formInput} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. MD, PhD, QC Director" />
              </div>
              <div>
                <label style={s.formLabel}>{t2.specialty}</label>
                <input style={s.formInput} value={form.specialty} onChange={e => setForm(f => ({ ...f, specialty: e.target.value }))} placeholder="e.g. Pharmaceutical Chemistry" />
              </div>

              {/* Signature upload */}
              <div>
                <label style={s.formLabel}>{t2.signature}</label>
                <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginBottom: 8 }}>{t2.sigHint}</p>
                {sigPreview ? (
                  <div style={s.sigPreviewWrap}>
                    <img src={sigPreview} alt="Signature preview" style={s.sigPreviewImg} />
                    <button style={s.sigChangeBtn} onClick={() => sigRef.current.click()}>{t2.changeSig}</button>
                  </div>
                ) : (
                  <button style={s.sigUploadBtn} onClick={() => sigRef.current.click()}>
                    ✍️ {t2.uploadSig}
                  </button>
                )}
                <input ref={sigRef} type="file" accept="image/png,image/jpeg" style={{ display: 'none' }} onChange={handleSigUpload} />
              </div>
            </div>

            <div style={s.modalActions}>
              <button style={s.cancelBtn} onClick={() => setShowForm(false)}>{t2.cancel}</button>
              <button style={s.confirmApproveBtn} onClick={handleSave} disabled={!form.name.trim() || saving}>
                {saving ? '...' : t2.save}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Step 0: Edit client data ──────────────────────────────────────────────────
function EditDataStep({ cert, editedSample, editedContact, setEditedSample, setEditedContact,
  editedLabelImage, setEditedLabelImage, editedLabelUrl, setEditedLabelUrl, lang }) {
  const de = lang === 'de';
  const sm = editedSample || {};
  const ct = editedContact || {};
  const labelFileRef = React.useRef(null);

  const setSm  = (k) => (e) => setEditedSample(p => ({ ...p, [k]: e.target.value }));
  const setCt  = (k) => (e) => setEditedContact(p => ({ ...p, [k]: e.target.value }));

  // Multi-select toggle for arrays (sampleType, methodology)
  const toggleArr = (key, val) => setEditedSample(p => {
    const arr = Array.isArray(p[key]) ? p[key] : [];
    return { ...p, [key]: arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val] };
  });

  const handleLabelFile = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setEditedLabelImage(ev.target.result);
    reader.readAsDataURL(file);
  };

  const inp  = { width:'100%', background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, padding:'8px 11px', color:'#fff', fontSize:13, outline:'none', boxSizing:'border-box', fontFamily:'inherit' };
  const sel  = { ...inp, background:'#0A1A4E', cursor:'pointer' };
  const lbl  = { fontSize:11.5, fontWeight:600, color:'rgba(255,255,255,0.55)', display:'block', marginBottom:5 };
  const sec  = { fontSize:11, fontWeight:700, color:'#4A9EFF', textTransform:'uppercase', letterSpacing:0.8, margin:'18px 0 10px', paddingBottom:5, borderBottom:'1px solid rgba(74,158,255,0.15)' };
  const grid = { display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px,1fr))', gap:12 };
  const chip = (active) => ({ padding:'5px 12px', borderRadius:20, cursor:'pointer', fontSize:12, fontWeight:600, fontFamily:'inherit', border: active ? '1px solid #1E5EFF' : '1px solid rgba(255,255,255,0.1)', background: active ? 'rgba(30,94,255,0.2)' : 'rgba(255,255,255,0.04)', color: active ? '#fff' : 'rgba(255,255,255,0.55)' });

  const LINES      = [['human','Human'],['veterinary','Veterinary'],['agricultural','Agricultural']];
  const CATEGORIES = [['medication','Medication'],['cosmetic','Cosmetic'],['supplement','Supplement'],['phytotherapy','Phytotherapy'],['homeopathic','Homeopathic'],['other','Other']];
  const SAMPLE_TYPES = ['MP','PG','PT','DM','MEE','AP','APT','CEA'];
  const METHODOLOGIES = ['USP','BP','EP','ISO','Client','Others'];
  const STORAGE_OPTS = [['ambient','Ambient 25±5°C'],['refrigeration','Refrigeration 2-8°C'],['light-sensitive','Light-sensitive']];
  const CERT_OPTS = [['SI','Yes'],['NO','No']];

  const currentLabel = editedLabelImage || editedLabelUrl;

  return (
    <div>
      <p style={{ fontSize:13, color:'rgba(255,255,255,0.5)', marginBottom:16 }}>
        {de ? 'Alle Felder prüfen und ggf. korrigieren.' : 'Review and edit all client-submitted fields before approval.'}
      </p>

      {/* ── Contact ── */}
      {ct && Object.keys(ct).length > 0 && (<>
        <div style={sec}>{de ? 'Kontaktdaten' : 'Contact Information'}</div>
        <div style={grid}>
          {[['companyName', de?'Firma':'Company Name'],['address', de?'Adresse':'Address'],['requestDate', de?'Datum':'Request Date']]
            .filter(([k]) => ct[k] !== undefined)
            .map(([k, label]) => (
              <div key={k}><label style={lbl}>{label}</label>
                <input style={inp} value={ct[k]||''} onChange={setCt(k)} />
              </div>
          ))}
        </div>
      </>)}

      {/* ── Product identification ── */}
      {sm && Object.keys(sm).length > 0 && (<>
        <div style={sec}>{de ? 'Produktidentifikation' : 'Product Identification'}</div>
        <div style={grid}>
          {[
            ['productName','Product Name'],['lot','Lot / Batch'],
            ['activeIngredient','Active Ingredient'],['doseConcentration','Dose / Concentration'],
            ['sanitaryRegistry','Sanitary Registry'],['supplier','Supplier'],
            ['manufacturer','Manufacturer / Country'],['pharmaceuticalForm','Pharmaceutical Form'],
            ['sampleQty','Sample Quantity'],
          ].map(([k, label]) => sm[k] !== undefined && (
            <div key={k}><label style={lbl}>{label}</label>
              <input style={inp} value={sm[k]||''} onChange={setSm(k)} />
            </div>
          ))}
        </div>

        {/* ── Dates ── */}
        <div style={sec}>{de ? 'Daten' : 'Dates'}</div>
        <div style={grid}>
          {[['mfgDate','Manufacturing Date'],['expDate','Expiration Date'],['samplingDate','Sampling Date']].map(([k,label]) => (
            <div key={k}><label style={lbl}>{label}</label>
              <input style={inp} type="date" value={sm[k]||''} onChange={setSm(k)} />
            </div>
          ))}
        </div>

        {/* ── Classification ── */}
        <div style={sec}>{de ? 'Klassifikation' : 'Classification'}</div>
        <div style={grid}>
          <div>
            <label style={lbl}>{de?'Produktlinie':'Product Line'}</label>
            <select style={sel} value={sm.line||''} onChange={setSm('line')}>
              <option value="">-- Select --</option>
              {LINES.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>{de?'Kategorie':'Category'}</label>
            <select style={sel} value={sm.category||''} onChange={setSm('category')}>
              <option value="">-- Select --</option>
              {CATEGORIES.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>{de?'Herstellerzertifikat':'Manufacturer Certificate'}</label>
            <select style={sel} value={sm.hasManufacturerCert||''} onChange={setSm('hasManufacturerCert')}>
              <option value="">-- Select --</option>
              {CERT_OPTS.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>

        {/* ── Storage & Transport ── */}
        <div style={sec}>{de ? 'Lagerung & Transport' : 'Storage & Transport'}</div>
        <div style={grid}>
          <div>
            <label style={lbl}>{de?'Lagerbedingungen':'Storage Conditions'}</label>
            <select style={sel} value={sm.storageCondition||''} onChange={setSm('storageCondition')}>
              <option value="">-- Select --</option>
              {STORAGE_OPTS.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>{de?'Transporttemperatur':'Transport Temperature'}</label>
            <input style={inp} value={sm.transportTemp||''} onChange={setSm('transportTemp')} placeholder="e.g. 2-8°C" />
          </div>
        </div>

        {/* ── Sample Type (multi) ── */}
        <div style={sec}>{de ? 'Probentyp' : 'Sample Type'}</div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:4 }}>
          {SAMPLE_TYPES.map(v => (
            <button key={v} style={chip(Array.isArray(sm.sampleType) && sm.sampleType.includes(v))}
              onClick={() => toggleArr('sampleType', v)}>{v}</button>
          ))}
        </div>
        <div style={{ fontSize:10.5, color:'rgba(255,255,255,0.3)', marginBottom:8 }}>
          MP=Raw material · PG=Bulk · PT=Finished · DM=Medical device · MEE=Packaging · AP=Purified water · APT=Drinking water · CEA=Other
        </div>

        {/* ── Methodology (multi) ── */}
        <div style={sec}>{de ? 'Methodik' : 'Analytical Methodology'}</div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:8 }}>
          {METHODOLOGIES.map(v => (
            <button key={v} style={chip(Array.isArray(sm.methodology) && sm.methodology.includes(v))}
              onClick={() => toggleArr('methodology', v)}>{v}</button>
          ))}
        </div>
      </>)}

      {/* ── Product Label ── */}
      <div style={sec}>🏷️ {de ? 'Produktetikett' : 'Product Label'}</div>
      {currentLabel ? (
        <div style={{ marginBottom:12 }}>
          <img
            src={currentLabel}
            alt="label"
            style={{ maxWidth:'100%', maxHeight:180, borderRadius:10, border:'1px solid rgba(255,255,255,0.1)', display:'block', marginBottom:8 }}
          />
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={() => labelFileRef.current.click()}
              style={{ background:'rgba(30,94,255,0.12)', border:'1px solid rgba(30,94,255,0.25)', color:'#4A9EFF', padding:'7px 14px', borderRadius:8, cursor:'pointer', fontSize:12, fontFamily:'inherit' }}>
              📷 {de ? 'Etikett ersetzen' : 'Replace label'}
            </button>
            <button onClick={() => { setEditedLabelImage(null); setEditedLabelUrl(null); }}
              style={{ background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.2)', color:'#F87171', padding:'7px 14px', borderRadius:8, cursor:'pointer', fontSize:12, fontFamily:'inherit' }}>
              ✕ {de ? 'Entfernen' : 'Remove'}
            </button>
          </div>
          {editedLabelImage && (
            <div style={{ fontSize:11, color:'#34D399', marginTop:6 }}>✓ {de ? 'Neue Etikettbild ausgewählt' : 'New label selected — will upload on approval'}</div>
          )}
        </div>
      ) : (
        <button onClick={() => labelFileRef.current.click()}
          style={{ background:'rgba(255,255,255,0.03)', border:'2px dashed rgba(255,255,255,0.1)', color:'rgba(255,255,255,0.45)', padding:'16px 24px', borderRadius:10, cursor:'pointer', fontSize:13, fontFamily:'inherit', width:'100%', marginBottom:8 }}>
          📎 {de ? 'Etikett hochladen' : 'Upload product label'}
        </button>
      )}
      <input ref={labelFileRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display:'none' }} onChange={handleLabelFile} />
    </div>
  );
}


// ── Color helper for avatars ──────────────────────────────────────────────────
const AVATAR_COLORS = ['#1E5EFF','#7C3AED','#DB2777','#D97706','#059669','#DC2626','#0891B2'];
const getAvatarColor = (str) => AVATAR_COLORS[(str?.charCodeAt(0)||0) % AVATAR_COLORS.length];
const getInitials = (name, email) => {
  const src = name || email || '?';
  return src.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
};

// ── UsersTab ──────────────────────────────────────────────────────────────────
function UsersTab({ lang }) {
  const de = lang === 'de';
  const [users,     setUsers]     = React.useState([]);
  const [loading,   setLoading]   = React.useState(true);
  const [showForm,  setShowForm]  = React.useState(false);
  const [saving,    setSaving]    = React.useState(false);
  const [search,    setSearch]    = React.useState('');
  const [form,      setForm]      = React.useState({ name:'', email:'', password:'' });
  const [error,     setError]     = React.useState('');
  const [success,   setSuccess]   = React.useState('');

  const load = async () => {
    setLoading(true);
    try { setUsers(await getAllUsers()); } catch(e) { console.error(e); }
    setLoading(false);
  };

  React.useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    setError(''); setSuccess('');
    if (!form.name.trim() || !form.email.trim() || form.password.length < 6) {
      setError(de ? 'Name, E-Mail und Passwort (min. 6 Zeichen) erforderlich.' : 'Name, email and password (min 6 chars) are required.');
      return;
    }
    setSaving(true);
    try {
      await adminCreateUser(form.name.trim(), form.email.trim(), form.password);
      setSuccess(de ? `Benutzer ${form.email} erstellt!` : `User ${form.email} created!`);
      setForm({ name:'', email:'', password:'' });
      setShowForm(false);
      load();
    } catch(e) {
      setError(e.message || 'Error creating user');
    }
    setSaving(false);
  };

  const filtered = users.filter(u => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
  });

  const inp = { width:'100%', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:10, padding:'10px 14px', color:'#fff', fontSize:13, outline:'none', boxSizing:'border-box', fontFamily:'inherit' };

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:'#fff' }}>
            {de ? 'Benutzerverwaltung' : 'User Management'}
          </h2>
          <p style={{ margin:'4px 0 0', fontSize:13, color:'rgba(255,255,255,0.4)' }}>
            {users.length} {de ? 'Benutzer registriert' : 'users registered'}
          </p>
        </div>
        <button
          onClick={() => { setShowForm(f=>!f); setError(''); setSuccess(''); }}
          style={{ background:'linear-gradient(135deg,#1E5EFF,#0A3DD4)', border:'none', color:'#fff', padding:'10px 20px', borderRadius:10, fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
          {showForm ? '✕ Cancel' : `+ ${de ? 'Benutzer erstellen' : 'Create User'}`}
        </button>
      </div>

      {/* Create user form */}
      {showForm && (
        <div style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:14, padding:20, marginBottom:20 }}>
          <h3 style={{ margin:'0 0 16px', fontSize:16, color:'#fff' }}>
            {de ? 'Neuen Benutzer erstellen' : 'Create New User'}
          </h3>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:14 }}>
            <div>
              <label style={{ fontSize:11.5, color:'rgba(255,255,255,0.5)', display:'block', marginBottom:5 }}>
                {de ? 'Vollständiger Name' : 'Full Name'} *
              </label>
              <input style={inp} placeholder="John Doe" value={form.name}
                onChange={e => setForm(p=>({...p, name:e.target.value}))} />
            </div>
            <div>
              <label style={{ fontSize:11.5, color:'rgba(255,255,255,0.5)', display:'block', marginBottom:5 }}>
                Email *
              </label>
              <input style={inp} type="email" placeholder="user@email.com" value={form.email}
                onChange={e => setForm(p=>({...p, email:e.target.value}))} />
            </div>
            <div>
              <label style={{ fontSize:11.5, color:'rgba(255,255,255,0.5)', display:'block', marginBottom:5 }}>
                {de ? 'Passwort' : 'Password'} * (min 6)
              </label>
              <input style={inp} type="password" placeholder="••••••••" value={form.password}
                onChange={e => setForm(p=>({...p, password:e.target.value}))} />
            </div>
          </div>
          {error   && <div style={{ color:'#F87171', fontSize:13, marginBottom:10 }}>⚠️ {error}</div>}
          {success && <div style={{ color:'#34D399', fontSize:13, marginBottom:10 }}>✓ {success}</div>}
          <button
            onClick={handleCreate} disabled={saving}
            style={{ background:'linear-gradient(135deg,#22C55E,#16A34A)', border:'none', color:'#fff', padding:'11px 28px', borderRadius:10, fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:'inherit', opacity:saving?0.7:1 }}>
            {saving ? '...' : (de ? 'Benutzer erstellen' : 'Create User')}
          </button>
        </div>
      )}

      {success && !showForm && (
        <div style={{ background:'rgba(52,211,153,0.1)', border:'1px solid rgba(52,211,153,0.3)', borderRadius:10, padding:'12px 16px', marginBottom:16, color:'#34D399', fontSize:13 }}>
          ✓ {success}
        </div>
      )}

      {/* Search */}
      <div style={{ position:'relative', marginBottom:16 }}>
        <span style={{ position:'absolute', left:14, top:'50%', transform:'translateY(-50%)', fontSize:16, opacity:0.4 }}>🔍</span>
        <input
          style={{ ...inp, paddingLeft:42 }}
          placeholder={de ? 'Benutzer suchen...' : 'Search users...'}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && <button onClick={()=>setSearch('')} style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', color:'rgba(255,255,255,0.4)', fontSize:18, cursor:'pointer' }}>✕</button>}
      </div>

      {/* User list */}
      {loading ? (
        <div style={{ textAlign:'center', padding:40, color:'rgba(255,255,255,0.4)' }}>Loading...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:40, color:'rgba(255,255,255,0.3)', fontSize:14 }}>
          {search ? `No users found for "${search}"` : (de ? 'Noch keine Benutzer. Erstellen Sie den ersten!' : 'No users yet. Create the first one!')}
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {filtered.map(u => (
            <div key={u.uid || u.id} style={{ display:'flex', alignItems:'center', gap:14, background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:12, padding:'12px 16px' }}>
              {/* Avatar */}
              <div style={{ width:44, height:44, borderRadius:'50%', background:getAvatarColor(u.email), display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, fontSize:16, color:'#fff', flexShrink:0 }}>
                {getInitials(u.name, u.email)}
              </div>
              {/* Info */}
              <div style={{ flex:1 }}>
                <div style={{ fontSize:14, fontWeight:600, color:'#fff' }}>{u.name || '—'}</div>
                <div style={{ fontSize:12, color:'rgba(255,255,255,0.4)', marginTop:2 }}>{u.email}</div>
              </div>
              {/* Meta */}
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.25)', textAlign:'right' }}>
                <div>{u.role || 'user'}</div>
                {u.createdAt?.seconds && (
                  <div>{new Date(u.createdAt.seconds*1000).toLocaleDateString()}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


const s = {
  page:        { minHeight: '100vh', background: '#070F2B', padding: '84px 24px 60px', maxWidth: 960, margin: '0 auto', fontFamily: "'Inter', sans-serif", color: '#fff' },
  title:       { fontSize: 28, fontWeight: 800, color: '#fff', marginBottom: 24 },
  tabBar:      { display: 'flex', gap: 4, marginBottom: 24, background: 'rgba(255,255,255,0.04)', borderRadius: 14, padding: 4, flexWrap: 'wrap' },
  tab:         { flex: 1, background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 13, fontWeight: 600, padding: '11px 16px', borderRadius: 10, cursor: 'pointer', fontFamily: 'inherit', minWidth: 120 },
  tabActive:   { background: 'rgba(255,255,255,0.08)', color: '#fff' },
  empty:       { textAlign: 'center', color: 'rgba(255,255,255,0.4)', padding: 60, fontSize: 15 },
  list:        { display: 'flex', flexDirection: 'column', gap: 16 },
  card:        { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: 22 },
  cardTop:     { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 },
  certId:      { fontFamily: 'monospace', fontSize: 12, color: '#4A9EFF', display: 'block', marginBottom: 4 },
  product:     { fontSize: 18, fontWeight: 700, color: '#fff', margin: 0 },
  metaGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px,1fr))', gap: 12, marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid rgba(255,255,255,0.06)' },
  metaLabel:   { fontSize: 10.5, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 },
  metaValue:   { fontSize: 13, color: '#fff', fontWeight: 500, wordBreak: 'break-word' },
  sampleBlock: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '10px 14px', marginBottom: 12 },
  sampleRow:   { display: 'flex', gap: 12, padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', flexWrap: 'wrap' },
  details:     { marginBottom: 12 },
  summary:     { cursor: 'pointer', fontSize: 13, color: '#4A9EFF', fontWeight: 600, padding: '6px 0' },
  specsTable:  { width: '100%', borderCollapse: 'collapse', marginTop: 8 },
  specsHead:   { padding: '6px 8px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, color: 'rgba(255,255,255,0.4)', borderBottom: '1px solid rgba(255,255,255,0.07)' },
  specsTd:     { padding: '6px 8px', fontSize: 12, color: 'rgba(255,255,255,0.7)' },
  paymentBox:  { background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 10, padding: '14px 16px', marginBottom: 12 },
  paymentTitle:{ fontSize: 13, fontWeight: 700, color: '#FBBF24', marginBottom: 10 },
  paymentRow:  { display: 'flex', gap: 12, alignItems: 'flex-start', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', flexWrap: 'wrap' },
  paymentLabel:{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 0.4, minWidth: 60, paddingTop: 1 },
  paymentVal:  { fontSize: 13, color: '#fff', flex: 1 },
  txHashCode:  { fontFamily: 'monospace', fontSize: 11, color: '#4A9EFF', wordBreak: 'break-all', background: 'rgba(74,158,255,0.08)', padding: '3px 7px', borderRadius: 6 },
  explorerLink:{ fontSize: 12, color: '#4A9EFF', textDecoration: 'none', fontWeight: 600, whiteSpace: 'nowrap' },
  doctorBadge: { background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: '#6EE7B7', marginBottom: 10 },
  rejectedNote:{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 8, padding: '10px 12px', fontSize: 12.5, color: '#FCA5A5', marginBottom: 4 },
  cardActions: { display: 'flex', gap: 10, marginTop: 8 },
  approveBtn:  { flex: 1, background: 'linear-gradient(135deg,#22C55E,#16A34A)', border: 'none', color: '#fff', padding: '11px', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  rejectBtn:   { flex: 1, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#F87171', padding: '11px', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  reEditBtn:   { width: '100%', background: 'rgba(74,158,255,0.1)', border: '1px solid rgba(74,158,255,0.25)', color: '#4A9EFF', padding: '9px', borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  deniedBox:   { textAlign: 'center', padding: '120px 24px' },
  deniedTitle: { fontSize: 22, fontWeight: 800, color: '#fff', marginBottom: 8 },
  deniedText:  { fontSize: 14, color: 'rgba(255,255,255,0.5)' },
  toast:       { background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: 10, padding: '12px 16px', color: '#6EE7B7', fontSize: 14, marginBottom: 20 },

  // Modals
  modalOverlay:       { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 20 },
  modal:              { background: '#0E1840', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 18, padding: 28, maxWidth: 480, width: '100%', maxHeight: '90vh', overflowY: 'auto' },
  modalTitle:         { fontSize: 19, fontWeight: 800, color: '#fff', marginBottom: 8 },
  modalDesc:          { fontSize: 13.5, color: 'rgba(255,255,255,0.55)', marginBottom: 14 },
  modalCertId:        { fontSize: 13, color: '#4A9EFF', fontFamily: 'monospace', marginBottom: 16 },
  modalActions:       { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 },
  cancelBtn:          { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', padding: '10px 20px', borderRadius: 10, fontSize: 14, cursor: 'pointer', fontWeight: 600 },
  confirmRejectBtn:   { background: '#EF4444', border: 'none', color: '#fff', padding: '10px 20px', borderRadius: 10, fontSize: 14, cursor: 'pointer', fontWeight: 700 },
  confirmApproveBtn:  { background: 'linear-gradient(135deg,#22C55E,#16A34A)', border: 'none', color: '#fff', padding: '10px 20px', borderRadius: 10, fontSize: 14, cursor: 'pointer', fontWeight: 700 },
  textarea:           { width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, padding: 12, color: '#fff', fontSize: 14, outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' },
  noDoctorsNote:      { background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 10, padding: '14px', fontSize: 13, color: '#FBBF24', marginBottom: 8 },
  doctorList:         { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 },
  doctorOption:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '12px 14px', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s' },
  doctorOptionActive: { background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.35)' },
  doctorOptionLeft:   { display: 'flex', alignItems: 'center', gap: 12 },
  doctorOptionName:   { fontSize: 14, fontWeight: 700, color: '#fff', textAlign: 'left' },
  doctorOptionTitle:  { fontSize: 12, color: 'rgba(255,255,255,0.45)', textAlign: 'left' },
  doctorSigThumb:     { width: 60, height: 28, objectFit: 'contain', background: '#fff', borderRadius: 6, padding: 2 },
  doctorSigPlaceholder:{ width: 60, height: 28, background: 'rgba(255,255,255,0.05)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 },

  // Doctors tab
  addDoctorBtn:  { background: 'linear-gradient(135deg,#1E5EFF,#0A3DD4)', border: 'none', color: '#fff', padding: '10px 20px', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  doctorGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px,1fr))', gap: 16 },
  doctorCard:    { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: 20 },
  doctorCardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 },
  doctorName:    { fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 3 },
  doctorTitle:   { fontSize: 13, color: '#4A9EFF', fontWeight: 600 },
  doctorSpecialty:{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginTop: 3 },
  sigSection:    { borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: 12 },
  sigImg:        { maxWidth: '100%', maxHeight: 60, objectFit: 'contain', background: '#fff', borderRadius: 8, padding: 6, display: 'block' },
  noSig:         { fontSize: 12, color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' },
  editBtn:       { background: 'rgba(74,158,255,0.1)', border: '1px solid rgba(74,158,255,0.25)', color: '#4A9EFF', padding: '5px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  deleteBtn:     { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', color: '#F87171', padding: '5px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12 },
  formLabel:     { display: 'block', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.65)', marginBottom: 6 },
  formInput:     { width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '11px 14px', color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
  sigPreviewWrap:{ background: '#fff', borderRadius: 10, padding: 10, display: 'inline-block' },
  sigPreviewImg: { maxWidth: 200, maxHeight: 70, objectFit: 'contain', display: 'block' },
  sigChangeBtn:  { display: 'block', marginTop: 8, background: 'none', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' },
  wizardSteps:     { display:'flex', alignItems:'center', gap:0, marginBottom:0 },
  wizardStep:      { display:'flex', flexDirection:'column', alignItems:'center', gap:2, background:'none', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, padding:'6px 8px', cursor:'pointer', fontFamily:'inherit', minWidth:0, flex:'1 1 0', transition:'all 0.2s' },
  wizardStepActive:{ background:'rgba(30,94,255,0.2)', border:'1px solid #1E5EFF' },
  wizardStepDone:  { background:'rgba(52,211,153,0.1)', border:'1px solid rgba(52,211,153,0.3)' },
  wizardStepNum:   { fontSize:12, fontWeight:800, color:'rgba(255,255,255,0.5)', width:20, height:20, borderRadius:10, background:'rgba(255,255,255,0.08)', display:'flex', alignItems:'center', justifyContent:'center' },
  wizardStepLabel: { fontSize:8.5, color:'rgba(255,255,255,0.6)', textAlign:'center', lineHeight:1.3 },
  wizardLine:      { flex:1, height:2, background:'rgba(255,255,255,0.08)', minWidth:8 },
  wizardLineDone:  { background:'rgba(52,211,153,0.4)' },
  approveTabBar:  { display:'flex', gap:4, marginBottom:20, background:'rgba(255,255,255,0.04)', borderRadius:10, padding:4 },
  approveTab:     { flex:1, background:'none', border:'none', color:'rgba(255,255,255,0.5)', fontSize:12, fontWeight:600, padding:'9px 8px', borderRadius:8, cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap' },
  approveTabActive:{ background:'rgba(255,255,255,0.1)', color:'#fff' },
  resultsGrid:    { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:12, marginBottom:4 },
  paramRow:       { display:'flex', alignItems:'center', gap:8, padding:'6px 0', borderBottom:'1px solid rgba(255,255,255,0.05)', flexWrap:'wrap' },
  paramName:      { fontSize:12, color:'rgba(255,255,255,0.7)', minWidth:120, fontWeight:600 },
  paramSpec:      { fontSize:11, color:'rgba(255,255,255,0.35)', minWidth:60 },
  paramInput:     { flex:1, background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:7, padding:'6px 10px', color:'#fff', fontSize:12, outline:'none', fontFamily:'inherit', minWidth:80 },
  paramPass:      { background:'#0A1A4E', border:'1px solid rgba(255,255,255,0.15)', color:'#fff', borderRadius:7, padding:'6px 8px', fontSize:11, cursor:'pointer', fontFamily:'inherit' },
  sigUploadBtn:  { background: 'rgba(255,255,255,0.04)', border: '2px dashed rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.5)', padding: '14px 20px', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontFamily: 'inherit', width: '100%' },
};
